// Builds the human-readable `stepAction` stored on each history step and shown
// in the history panel and the undo/redo tooltips.
//
// Messages name the field *and* what it became — "Changed partner name to 'Park
// Hyatt'" rather than "Edit partner name" — so the log answers what happened
// without opening the record.

/** Longest value rendered inline; descriptions are far longer than this. */
const MAX_VALUE_CHARS = 60;

/**
 * Renders a value for a message. Returning `null` drops the value clause
 * entirely ("Changed hero image"), which is what you want for a field whose
 * value is a URL nobody can read at a glance. Returning `undefined` falls back
 * to the default rendering for the value's type.
 *
 * It takes the value rather than closing over one so undo/redo can describe the
 * value being *restored*, not the one that was originally set.
 */
export type ValueFormatterType = (value: unknown) => string | null | undefined;

/** Formatter for a select backed by a `Record<value, label>` map. */
export function labelFormatter(labels: Record<string, string>): ValueFormatterType {
  return (value) => (typeof value === 'string' ? labels[value] : undefined);
}

/**
 * Formatter for a list field, counting entries rather than listing them:
 * `countFormatter('image')` gives "5 images". The count is the part anyone
 * reading the log can act on; the URLs are not.
 *
 * Counts a map as readily as an array, since a list stored so its rows can be
 * addressed one at a time — `ExperienceType.optionalServices` — still reads to a
 * human as a list of that many things.
 */
export function countFormatter(noun: string): ValueFormatterType {
  return (value) => {
    const count = Array.isArray(value)
      ? value.length
      : value !== null && typeof value === 'object'
        ? Object.keys(value).length
        : undefined;
    if (count === undefined) return undefined;
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
  };
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
}

/** Collapses newlines and clips, so one step stays one line in the panel. */
function truncate(text: string): string {
  const oneLine = text.replace(/\s+/g, ' ').trim();
  return oneLine.length > MAX_VALUE_CHARS ? `${oneLine.slice(0, MAX_VALUE_CHARS)}…` : oneLine;
}

/**
 * `describeChange('partner name', 'Park Hyatt')` -> "Changed partner name to 'Park
 * Hyatt'". Quotes mark text the user typed verbatim; formatted values (a status
 * label, a price) are shown unquoted.
 */
export function describeChange(label: string, value: unknown, format?: ValueFormatterType): string {
  if (typeof value === 'boolean') return `Turned ${label} ${value ? 'on' : 'off'}`;
  if (isEmpty(value)) return `Cleared ${label}`;

  const display = format?.(value);
  if (display === null) return `Changed ${label}`;
  if (display !== undefined) return `Changed ${label} to ${display}`;

  if (Array.isArray(value)) {
    return `Changed ${label} to ${value.length} ${value.length === 1 ? 'item' : 'items'}`;
  }
  if (typeof value === 'number') return `Changed ${label} to ${value}`;
  return `Changed ${label} to '${truncate(String(value))}'`;
}

/**
 * Message for a save that covers several fields at once, as on the partner's
 * own profile page: "Partner updated Accounting (accounting email, billing address)".
 * Falls back to the bare heading when nothing is named.
 */
export function describeSectionSave(who: string, section: string, changedLabels: string[]): string {
  const head = `${who} updated ${section}`;
  return changedLabels.length > 0 ? `${head} (${changedLabels.join(', ')})` : head;
}
